import { useState } from "react";
import { KEY_OWN_CID, KEY_TOKEN } from "./challengeModel";
import { Label, Spacer } from "../editor/EditorHelpers";
import { TextEditor } from "../components/TextEditor";
import { SmallText, StatusText } from "./ChallengeHelpers";
import { PlainButtonBar, PrimaryButton, SecondaryButton } from "../components/Button";
import styled from "styled-components";
import { ConfirmDialog, ConfirmDialogProps } from "../components/ConfirmDialog";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useOwnCid } from "./useOwnCid";


interface SettingsProps {
  currentName: string;
  censoredEmail: string;
  refresh: () => void;
}

export const ChallengeSettings: React.FC<SettingsProps> = ({ currentName, censoredEmail, refresh }) => {
  const { t } = useTranslation();
  const [name, setName] = useState<string>(currentName || "");
  const [savedName, setSavedName] = useState<string>(currentName || "");
  const [email, setEmail] = useState<string>(censoredEmail || "");
  const [savedEmail, setSavedEmail] = useState<string>(censoredEmail || "");
  const [error, setError] = useState<string>("");
  const navigate = useNavigate();
  const { setOwnCid } = useOwnCid();
  const [confirmDialogProps, setConfirmDialogProps] = useState<ConfirmDialogProps>({
    isOpen: false,
    title: "",
    message: "",
    onCancel: () => setConfirmDialogProps((prev) => {
      return {
        ...prev,
        isOpen: false
      }
    })
  });

  const setNameAndMaybeClearError = (v: string) => {
    if (v.length > 0) {
      setError("");
    }
    setName(v);
  }

  const handleSave = () => {
    setSavedName(name);
    setSavedEmail(email);
    const formData: Record<string, string> = {
      own_cid: localStorage.getItem(KEY_OWN_CID),
      token: localStorage.getItem(KEY_TOKEN)
    };
    if (name !== savedName) {
      formData['name'] = name;
    }
    if (email !== savedEmail) {
      formData['email'] = email;
    }
    fetch('/api/update_challenge.php', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded'
      },
      body: new URLSearchParams(formData)
    })
      .then(response => response.json())
      .then(data => {
        if (data.status) {
          console.log("update_challenge.php status:", data.status);
          refresh();
        } else {
          console.log("update_challenge.php error:", data.error);
        }
      })
      .catch(error => console.error('Error:', error));
      // TODO better error handling
  }

  const doLogOut = () => {
    console.log("log out of challenge");
    setOwnCid('');
    localStorage.removeItem(KEY_TOKEN);
    navigate("/challenge");
}

  const handleLogOut = () => {
    setConfirmDialogProps((prev) => {
      return {
        ...prev,
        isOpen: true,
        title: t('challenge.confirm-log-out.title'),
        message: t('challenge.confirm-log-out.message'),
        onConfirm: () => {
          doLogOut();
        }
      }
    });
  }

  const handleDelete = () => {

  }

  const saveDisabled = !name || (name === savedName && email === savedEmail);

  return (
    <SettingsContainer>
      <Label>{t('name-or-nickname.label')}</Label>
      <TextEditor
        requestFocus 
        value={name} onChange={(v) => setNameAndMaybeClearError(v)} />
      <Spacer $height="15px" />
      <Label>{t('email-optional.label')}</Label>
      <TextEditor
        value={email} onChange={(v) => setEmail(v)} />
      <SmallText>
        {t('challenge.email-privacy.smalltext')}
      </SmallText>

      {error && <StatusText>{error}</StatusText>}
      <PlainButtonBar style={{marginTop: '1.8rem'}}>
        <Spacer style={{flexGrow: 1}} />
        <SecondaryButton onClick={handleDelete}>{t('delete.button')}</SecondaryButton>
        <SecondaryButton onClick={handleLogOut}>{t('log-out.button')}</SecondaryButton>
        <PrimaryButton disabled={saveDisabled} onClick={handleSave}>{t('save-changes.button')}</PrimaryButton>
        <Spacer style={{flexGrow: 1}} />
      </PlainButtonBar>

      <ConfirmDialog {...confirmDialogProps} />

    </SettingsContainer>
  )
}

const SettingsContainer = styled.div`
  margin: 0.75rem 2.0rem 2rem 2rem;
`